import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { PencilIcon } from '@blade/ui-kit'
import { tradeSettingsModel } from '@root/features/trade/trade-settings'
import { useTranslation } from '@root/shared/localization'
import { Popover } from '@root/shared/ui-kit'

import { openQuickBuyTradeSettingsDialogAction } from '../../trade-settings/atoms'
import { TradeSettingsPresetSelector } from '../../trade-settings/ui/common/trade-settings-preset-selector'

import { QuickBuySectionsInputs } from './quick-buy-sections-inputs'

import type { PopoverProps } from '@root/shared/ui-kit'

type QuickBuyPresetPopoverProps = Pick<
  PopoverProps,
  'open' | 'children' | 'onOpenChange'
>

export const QuickBuyPresetPopover: React.FC<QuickBuyPresetPopoverProps> = ({
  children,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()

  const [presetState] = useAtom(tradeSettingsModel.networkSettingsPresetState)
  const openTradeSettings = useAction(openQuickBuyTradeSettingsDialogAction)

  const renderPresetRow = () => {
    if (!presetState.value) {
      return undefined
    }

    return (
      <PresetRow>
        <PresetTitle>
          <span>{presetState.value.emoji}</span>
          <span>{presetState.value.name}</span>
        </PresetTitle>
        <Divider />
        <EditButton
          type="button"
          onClick={() => {
            onOpenChange?.(false)
            openTradeSettings()
          }}
        >
          <PencilIcon size={16} />
          <span>{t('common.edit')}</span>
        </EditButton>
      </PresetRow>
    )
  }

  const renderContent = () => (
    <Content>
      <TradeSettingsPresetSelector />
      {renderPresetRow()}
      <QuickBuySectionsInputs />
      {/* <Actions>
        <ApplyButton type="button">
          {t('trade-settings.quick-buy.preset-popover.apply-changes')}
        </ApplyButton>
        <ResetButton type="button">
          <Refresh2Icon size={14} />
          <span>
            {t('trade-settings.quick-buy.preset-popover.reset-to-default')}
          </span>
        </ResetButton>
      </Actions> */}
    </Content>
  )

  return (
    <Root
      open={open}
      onOpenChange={onOpenChange}
      content={renderContent()}
      side="bottom"
      sideOffset={16}
      align="end"
      alignOffset={-14}
    >
      {children}
    </Root>
  )
}

const Root = styled(Popover)`
  min-width: 0;
  background: var(--background-2);
  border: 0.6px solid var(--background-3);
  border-radius: 12px;
  box-shadow: 0px 0px 17px var(--backgroundA-2);
`

const Content = styled.div`
  display: grid;
  gap: 12px;
  padding: 12px;
`

const PresetRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
`

const PresetTitle = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 4px;
`

const EditButton = styled.button`
  all: unset;
  cursor: pointer;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  color: var(--text-12);
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
`

const Divider = styled.div`
  margin-top: 8px;
  height: 1px;
  background: var(--background-3);
  border-radius: 2px;
`

const _Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
`

const _ApplyButton = styled.button`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;

  display: grid;
  justify-content: center;
  align-items: center;

  height: 32px;
  padding-inline: 10px;
  border-radius: 6px;
  background: #222;

  color: var(--text-11);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
`

const _ResetButton = styled.button`
  all: unset;
  cursor: pointer;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  height: 32px;

  color: var(--text-11);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`
